import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError, logApiError } from '@/utils/apiErrorHandler';

interface UseDataFetchOptions<T> {
  fetchFunction: (...args: any[]) => Promise<T>;
  dependencies?: any[];
  enabled?: boolean;
  retryOnError?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

interface UseDataFetchReturn<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: (...args: any[]) => Promise<void>;
  reset: () => void;
}

/**
 * Generic hook for data fetching with retry logic and error handling
 */
export function useDataFetch<T>({
  fetchFunction,
  dependencies = [],
  enabled = true,
  retryOnError = true,
  maxRetries = 3,
  retryDelay = 1000,
}: UseDataFetchOptions<T>): UseDataFetchReturn<T> {
  const { token } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const executeFetch = useCallback(async (...args: any[]): Promise<void> => {
    if (!enabled || !isMountedRef.current) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError(null);

    try {
      // Automatically inject token if the function expects it
      const result = await fetchFunction(...args, token);
      
      if (abortControllerRef.current.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result);
      setRetryCount(0);
    } catch (err) {
      if (abortControllerRef.current.signal.aborted || !isMountedRef.current) {
        return;
      }

      const parsedError = parseApiError(err);
      logApiError(err, 'useDataFetch');

      if (retryOnError && retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          if (isMountedRef.current) {
            executeFetch(...args);
          }
        }, retryDelay * Math.pow(2, retryCount));
      } else {
        setError(parsedError.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [fetchFunction, token, enabled, retryOnError, maxRetries, retryDelay, retryCount]);

  const refetch = useCallback(async (...args: any[]) => {
    setRetryCount(0);
    await executeFetch(...args);
  }, [executeFetch]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setRetryCount(0);
    setLoading(false);
  }, []);

  // Auto-fetch when dependencies change
  useEffect(() => {
    if (enabled) {
      executeFetch();
    }
  }, [enabled, ...dependencies]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    reset,
  };
}

/**
 * Hook for paginated data fetching
 */
export function usePaginatedData<T>({
  fetchFunction,
  initialLimit = 10,
  enabled = true,
  dependencies = [],
}: {
  fetchFunction: (params: { limit: number; offset: number }, token?: string) => Promise<{
    data: T[];
    hasMore: boolean;
    total: number;
  }>;
  initialLimit?: number;
  enabled?: boolean;
  dependencies?: any[];
}) {
  const { token } = useAuth();
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const loadMore = useCallback(async () => {
    if (!enabled || loading || !hasMore) return;

    console.log("loadMore called with:", { limit: initialLimit, offset });
    setLoading(true);
    setError(null);

    try {
      const result = await fetchFunction({ limit: initialLimit, offset }, token || undefined);
      
      setData(prev => [...prev, ...result.data]);
      setOffset(prev => prev + result.data.length);
      setHasMore(result.hasMore);
      setTotal(result.total);
    } catch (err) {
      const parsedError = parseApiError(err);
      logApiError(err, 'usePaginatedData');
      setError(parsedError.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, token, enabled, initialLimit, offset]);

  const refresh = useCallback(async () => {
    setData([]);
    setOffset(0);
    setHasMore(true);
    setError(null);
    setLoading(true);
    
    try {
      const result = await fetchFunction({ limit: initialLimit, offset: 0 }, token || undefined);
      
      setData(result.data);
      setOffset(result.data.length);
      setHasMore(result.hasMore);
      setTotal(result.total);
    } catch (err) {
      const parsedError = parseApiError(err);
      logApiError(err, 'usePaginatedData');
      setError(parsedError.message);
    } finally {
      setLoading(false);
    }
  }, [fetchFunction, token, initialLimit]);

  const reset = useCallback(() => {
    setData([]);
    setOffset(0);
    setHasMore(true);
    setError(null);
    setLoading(false);
    setIsInitialized(false);
  }, []);

  // Auto-load initial data when dependencies change
  useEffect(() => {
    console.log("usePaginatedData dependencies changed:", dependencies);
    if (enabled && !loading) {
      console.log("Resetting paginated data state due to dependency change");
      // Reset state when dependencies change
      setData([]);
      setOffset(0);
      setHasMore(true);
      setError(null);
      setIsInitialized(false);
    }
  }, dependencies);

  // Auto-load initial data only once
  useEffect(() => {
    console.log("usePaginatedData auto-load effect:", { enabled, isInitialized, loading });
    if (enabled && !isInitialized && !loading) {
      console.log("Triggering loadMore due to initialization");
      setIsInitialized(true);
      loadMore();
    }
  }, [enabled, isInitialized, loading, loadMore]);

  return {
    data,
    loading,
    error,
    hasMore,
    total,
    loadMore,
    refresh,
    reset,
  };
}
